/**
 * Agenda Calendar View Component
 * Fantastical-style split view with mini month calendar + scrollable event list
 */

import { useState, useMemo } from 'react'
import { Box, Paper, Typography, Divider } from '@mui/material'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  format,
  parseISO,
  isSameDay,
  isToday,
  isTomorrow,
  isYesterday,
  startOfDay,
  compareAsc,
} from 'date-fns'
import { CalendarEventDto, RsvpStatus } from '@api/types/calendarEvent'
import { EventIndicatorDay } from './EventIndicatorDay'
import { EventListItem } from './EventListItem'

interface AgendaCalendarViewProps {
  events: CalendarEventDto[]
  currentDate: Date
  onDateChange: (date: Date) => void
  onRsvp?: (eventId: string, status: RsvpStatus) => void
}

/**
 * Format date header with smart labels
 */
function formatDateHeader(date: Date): string {
  if (isToday(date)) return 'Today'
  if (isTomorrow(date)) return 'Tomorrow'
  if (isYesterday(date)) return 'Yesterday'
  return format(date, 'EEEE, MMMM d, yyyy')
}

/**
 * Group events by date
 */
function groupEventsByDate(
  events: CalendarEventDto[]
): Map<string, CalendarEventDto[]> {
  const grouped = new Map<string, CalendarEventDto[]>()

  events.forEach((event) => {
    const dateKey = format(parseISO(event.startDateTime), 'yyyy-MM-dd')
    const existing = grouped.get(dateKey) || []
    existing.push(event)
    grouped.set(dateKey, existing)
  })

  // Sort events within each day by start time
  grouped.forEach((dayEvents) => {
    dayEvents.sort((a, b) =>
      compareAsc(parseISO(a.startDateTime), parseISO(b.startDateTime))
    )
  })

  return grouped
}

export function AgendaCalendarView({
  events,
  currentDate,
  onDateChange,
  onRsvp,
}: AgendaCalendarViewProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(currentDate)

  // Group events by date for the list
  const eventsByDate = useMemo(() => groupEventsByDate(events), [events])

  // Get events for the selected date
  const selectedDateKey = format(selectedDate, 'yyyy-MM-dd')
  const eventsForSelectedDate = eventsByDate.get(selectedDateKey) || []

  // Get all dates that have events (sorted)
  const datesWithEvents = useMemo(() => {
    return Array.from(eventsByDate.keys())
      .map((dateStr) => parseISO(dateStr))
      .sort(compareAsc)
  }, [eventsByDate])

  // Filter to show events from selected date onwards
  const upcomingDates = useMemo(() => {
    const selectedStart = startOfDay(selectedDate)
    return datesWithEvents.filter(
      (date) => startOfDay(date) >= selectedStart
    )
  }, [datesWithEvents, selectedDate])

  const handleDateSelect = (date: Date | null) => {
    if (date) {
      setSelectedDate(date)
      onDateChange(date)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3,
        minHeight: 500,
      }}
    >
      {/* Mini Calendar */}
      <Paper
        sx={{
          flex: '0 0 auto',
          p: 1,
          borderRadius: 2,
          alignSelf: { xs: 'stretch', md: 'flex-start' },
        }}
        elevation={1}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            value={selectedDate}
            onChange={handleDateSelect}
            slots={{
              day: (dayProps) => (
                <EventIndicatorDay {...dayProps} events={events} />
              ),
            }}
            sx={{
              width: { xs: '100%', md: 320 },
              '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': {
                justifyContent: 'space-around',
              },
            }}
          />
        </LocalizationProvider>
      </Paper>

      {/* Event List */}
      <Paper
        sx={{
          flex: 1,
          overflow: 'auto',
          maxHeight: { xs: 'auto', md: '70vh' },
          p: 2,
          borderRadius: 2,
        }}
        elevation={1}
      >
        {/* Selected date header */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          {formatDateHeader(selectedDate)}
        </Typography>

        {/* Events for selected date */}
        {eventsForSelectedDate.length > 0 ? (
          <Box sx={{ mb: 3 }}>
            {eventsForSelectedDate.map((event) => (
              <EventListItem
                key={event.id}
                event={event}
                onRsvp={onRsvp}
              />
            ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, fontStyle: 'italic' }}
          >
            No events on this date
          </Typography>
        )}

        {/* Upcoming events section */}
        {upcomingDates.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="subtitle2"
              color="text.secondary"
              gutterBottom
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Upcoming
            </Typography>

            {upcomingDates
              .filter((date) => !isSameDay(date, selectedDate))
              .slice(0, 7) // Show next 7 days with events
              .map((date) => {
                const dateKey = format(date, 'yyyy-MM-dd')
                const dayEvents = eventsByDate.get(dateKey) || []

                return (
                  <Box key={dateKey} sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: isToday(date) ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {formatDateHeader(date)}
                    </Typography>
                    {dayEvents.map((event) => (
                      <EventListItem
                        key={event.id}
                        event={event}
                        onRsvp={onRsvp}
                      />
                    ))}
                  </Box>
                )
              })}
          </>
        )}

        {/* Empty state */}
        {events.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No events in this date range
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Select a date to see events or create a new one
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default AgendaCalendarView
